import { Package as PackageIcon } from 'lucide-react';
import { CYCLE_TYPE_LABELS } from '@hch/shared';
import { Badge } from '~/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '~/components/shared/states';
import { usePackages } from '~/lib/queries';
import { cn, formatDuration, formatPrice } from '~/lib/utils';
import type { BookingDraft } from '../booking-types';
import { StepShell } from '../step-shell';

/**
 * Tunnel de reservation — parcours principal de l'application.
 *
 * L'ordre des etapes suit litteralement l'enonce : adresse, cycle, operation de
 * maintenance, forfait, creneau, complements. Deux partis pris :
 *
 *   - La couverture est verifiee DES l'etape 1. Un client hors zone l'apprend
 *     immediatement, avant d'avoir saisi quoi que ce soit d'autre — plutot qu'apres
 *     cinq etapes, ce qui serait une perte de temps et une mauvaise experience.
 *
 *   - Le compte n'est jamais un prealable (RG-04). Un visiteur connecte se voit
 *     simplement proposer ses adresses et ses cycles enregistres.
 */

export function PackageStep({
  draft,
  patch,
  onBack,
  onNext,
}: {
  draft: BookingDraft;
  patch: (values: Partial<BookingDraft>) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  // Le type de cycle filtre les forfaits des la requete : un forfait incompatible
  // n'est jamais affiche, plutot que propose puis refuse a la validation.
  const {
    data: packages,
    isLoading,
    error,
    refetch,
  } = usePackages({
    serviceTypeId: draft.serviceTypeId,
    cycleType: draft.cycleType,
  });

  return (
    <StepShell
      title="Quel forfait ?"
      description="Duree et tarif sont fixes a l'avance. Aucune surprise a l'arrivee."
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!draft.packageId}
    >
      {isLoading ? <LoadingState rows={3} /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {packages && packages.length === 0 ? (
        <EmptyState
          icon={PackageIcon}
          title="Aucun forfait disponible"
          description="Aucun forfait ne correspond a ce type de cycle pour cette operation."
        />
      ) : null}

      <div className="grid gap-3">
        {packages?.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => patch({ packageId: item.id, slotIds: undefined })}
            className={cn(
              'flex flex-col gap-2 rounded-lg border p-4 text-left transition-colors hover:bg-accent',
              draft.packageId === item.id ? 'border-primary bg-accent' : 'border-border',
            )}
            aria-pressed={draft.packageId === item.id}
          >
            <div className="flex items-start justify-between gap-3">
              <p className="font-medium">{item.name}</p>
              <span className="shrink-0 font-semibold tabular-nums">{formatPrice(item.price)}</span>
            </div>
            <p className="text-sm text-muted-foreground">{item.description}</p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">{formatDuration(item.durationMin)}</Badge>
              {item.cycleType ? (
                <Badge variant="secondary">{CYCLE_TYPE_LABELS[item.cycleType]}</Badge>
              ) : (
                <Badge variant="secondary">Tous cycles</Badge>
              )}
            </div>
          </button>
        ))}
      </div>
    </StepShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Etape 5 — Creneau (RG-02 + RG-03)
// ═══════════════════════════════════════════════════════════════════════════════
